



























import { BootstrapButton, BootstrapImageProps, BootstrapLink, BootstrapImage } from '@movecloser/ui-core'
import { Component, Inject, Mixins, Prop, Ref } from 'vue-property-decorator'
import { v4 as uuid } from 'uuid'

import { isLink, log, toBootstrapImageProps } from '../../../../support'
import { Color, LinkWithLabel, Related, RelatedType } from '../../../../contracts'

import { AbstractModuleUiPresentation } from '../../_abstract'
import { Colorable } from '../../../../extensions'
import { BackgroundColorPalette } from '../../../containers/Container/Container.helpers'
import { ResolvesRelatedAsync } from '@/shared/modules/src/services'
import { DescriptionOfImageFile, ImageRatio } from '@/shared/modules/src/models'

/**
 * Presentational component for the `FloatingButtonModuleUi`.
 *
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 */
@Component<FloatingButtonModuleUiPresentation>({
  name: 'FloatingButtonModuleUiPresentation',
  components: {
    BootstrapButton,
    BootstrapImage,
    BootstrapLink,
  },
})
export class FloatingButtonModuleUiPresentation extends Mixins<
  AbstractModuleUiPresentation, Colorable
>(AbstractModuleUiPresentation, Colorable) {
  /**
   * Service capable of resolving the related data.
   */
   @Prop({ type: Object, required: true })
  public readonly relatedService!: ResolvesRelatedAsync

  /**
   * Link associated with the button.
   */
  @Prop({ type: Object, required: true })
  public readonly link!: LinkWithLabel

  @Prop({ type: String, required: false })
  public readonly backgroundColor!: string | null

  @Prop({ type: String, required: false })
  public readonly images!: { image: Related<RelatedType.File> }[] | null

  /**
   * Reference to the FloatingButton element.
   */
  @Ref() private readonly floatingButton!: HTMLElement

  /**
   * Unique ID for this FloatingButton instance.
   */
  public readonly buttonId: string = uuid()

  public imagesData: BootstrapImageProps[] = []

  /**
   * Determines whether the app is running on a mobile phone OR a tablet.
   */
  @Inject({ from: 'isMobile', default: false })
  private readonly isMobileDevice!: boolean

  /**
   * Determines whether there's a valid link to render.
   */
  public get hasLink (): boolean {
    return isLink(this.link)
  }

  /**
   * Computed styles for the FloatingButton.
   */
  public floatingButtonStyles: Record<string, string> | null = null

  /**
   * Combines floatingButtonStyles with visibility logic.
   */
  public get computedStyles (): Record<string, string> {
    return this.floatingButtonStyles
      ? this.floatingButtonStyles
      : { visibility: 'hidden' }
  }

  /**
   * Calculates the styles for the FloatingButton.
   */
  private calculateFloatingButtonStyles (): void {
    if (!this.floatingButton) {
      return
    }
    const allFloatingButtons = Array.from(document.querySelectorAll('.FloatingButton'))
    const isFirstInstance = allFloatingButtons[0]?.id === this.buttonId

    const baseOffset = 44 // 2.75rem in pixels
    const spacing = 15 // Space between buttons in pixels

    if (this.isMobileDevice) {
      const currentRightOffset = baseOffset

      for (let index = 0; index < allFloatingButtons.length; index++) {
        const buttonElement = allFloatingButtons[index] as HTMLElement

        if (buttonElement.id === this.buttonId) {
          if (isFirstInstance) {
            this.floatingButtonStyles = { right: `${currentRightOffset}px` }
          } else {
            const previousButton = allFloatingButtons[index - 1] as HTMLElement
            const previousRight = parseInt(previousButton.style.right || `${baseOffset}px`, 10)
            const previousWidth = previousButton.offsetWidth
            this.floatingButtonStyles = { right: `${previousRight + previousWidth + spacing}px` }
          }
          return
        }
      }
    } else {
      const currentBottomOffset = baseOffset

      for (let index = 0; index < allFloatingButtons.length; index++) {
        const buttonElement = allFloatingButtons[index] as HTMLElement

        if (buttonElement.id === this.buttonId) {
          if (isFirstInstance) {
            this.floatingButtonStyles = { bottom: `${currentBottomOffset}px` }
          } else {
            const previousButton = allFloatingButtons[index - 1] as HTMLElement
            const previousBottom = parseInt(previousButton.style.bottom || `${baseOffset}px`, 10)
            const previousHeight = previousButton.offsetHeight
            this.floatingButtonStyles = { bottom: `${previousBottom + previousHeight + spacing}px` }
          }
          return
        }
      }
    }
  }

  public readonly colorClassNameRegistry = {
    [Color.Black]: 'text-black',
    [Color.Body]: '',
    [Color.Violet]: 'text-custom',
    [Color.White]: 'text-white'
  }

  public get backgroundClassName (): string | undefined {
    switch (this.backgroundColor) {
      case BackgroundColorPalette.CTA:
        return '--bg-cta'
      case BackgroundColorPalette.DarkGray:
        return '--bg-dark-gray'
      case BackgroundColorPalette.LightGray:
        return '--bg-light-gray'
      case BackgroundColorPalette.None:
        return
      case BackgroundColorPalette.White:
        return '--bg-white'
    }
  }

  public async fetchRelated (): Promise<void> {
    if (!this.images || this.images.length === 0) {
      return
    }

    try {
      // Resolve all images concurrently using Promise.allSettled
      const results = await Promise.allSettled(
        this.images.map(async (image) => {
          const descriptionOfImageFile: DescriptionOfImageFile =
            await this.relatedService.resolve<DescriptionOfImageFile>(image.image)

          // Convert the resolved image to BootstrapImageProps
          return toBootstrapImageProps(descriptionOfImageFile, ImageRatio.Square)
        })
      )

      // Filter out successfully resolved images
      const resolvedImages = results
        .filter((result): result is PromiseFulfilledResult<BootstrapImageProps> => result.status === 'fulfilled')
        .map((result) => result.value)

      // Update imagesData with successfully resolved images
      this.imagesData = [...resolvedImages]

      // Log any errors for rejected promises
      const rejectedResults = results.filter((result) => result.status === 'rejected')
      if (rejectedResults.length > 0) {
        log(
          `FloatingButtonModuleUiPresentation.fetchRelated(): ${rejectedResults.length} images failed to resolve.`,
          'warn'
        )
      }
    } catch (error) {
      const message: string =
        'FloatingButtonModuleUiPresentation.fetchRelated(): Unexpected error occurred!'
      log([message, error], 'error')
    }
  }

  /**
   * Lifecycle hook to calculate styles after the component is mounted.
   */
  mounted (): void {
    this.$nextTick(() => {
      setTimeout(() => {
        this.calculateFloatingButtonStyles()
      }, 0)
    })

    this.fetchRelated()
  }
}

export default FloatingButtonModuleUiPresentation
